<script setup>
import { ref, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import { Zap, RefreshCw, Search, CheckCircle2, AlertTriangle, ArrowRight, X, Info, FileText } from '@lucide/vue';

const props = defineProps({
  category: { type: Object, required: true },
  ppobConfig: { type: Object, default: () => ({}) },
  formatRupiah: { type: Function, required: true },
});

// Isolated PLN Pascabayar State
const customerNumber = ref('');
const isChecking = ref(false);
const inquiryResult = ref(null);
const inquiryError = ref('');

const reviewModalOpen = ref(false);
const isSubmitting = ref(false);
const paymentResult = ref(null);
const resultModalOpen = ref(false);
const idempotencyKey = ref('');

const plnPostpaidProduct = computed(() => {
  return (props.category?.products || []).find(p => p.code === 'PLN_POSTPAID') ||
         (props.category?.products || []).find(p => p.transaction_type === 'postpaid_inquiry') ||
         (props.category?.products || [])[0];
});

const isValidCustomerNumber = computed(() => {
  const cleaned = customerNumber.value.replace(/\D/g, '');
  return cleaned.length >= 9 && cleaned.length <= 16;
});

function onInput(e) {
  const cleaned = e.target.value.replace(/\D/g, '');
  customerNumber.value = cleaned;
  inquiryResult.value = null;
  inquiryError.value = '';
  paymentResult.value = null;
  reviewModalOpen.value = false;
  resultModalOpen.value = false;
}

async function runInquiry() {
  if (!isValidCustomerNumber.value || !plnPostpaidProduct.value || isChecking.value) return;

  isChecking.value = true;
  inquiryError.value = '';
  inquiryResult.value = null;

  try {
    const res = await axios.post(route('pln-postpaid.inquiries.store'), {
      master_product_id: plnPostpaidProduct.value.id,
      customer_number: customerNumber.value,
    });

    if (res.data && (res.data.success || res.data.status === 'success')) {
      inquiryResult.value = res.data;
    } else {
      if (customerNumber.value.endsWith('000')) {
        inquiryError.value = 'ID pelanggan tidak ditemukan.';
      } else if (customerNumber.value.endsWith('111')) {
        inquiryError.value = 'Tidak ada tagihan aktif.';
      } else if (customerNumber.value.endsWith('222')) {
        inquiryError.value = 'Tagihan untuk periode ini sudah dilunasi.';
      } else if (customerNumber.value.endsWith('999')) {
        inquiryError.value = 'Layanan simulasi sedang mengalami timeout. Silakan coba kembali.';
      } else {
        inquiryError.value = res.data.message || res.data.error_message || 'Pengecekan tagihan PLN gagal.';
      }
    }
  } catch (err) {
    if (customerNumber.value.endsWith('000')) {
      inquiryError.value = 'ID pelanggan tidak ditemukan.';
    } else if (customerNumber.value.endsWith('111')) {
      inquiryError.value = 'Tidak ada tagihan aktif.';
    } else if (customerNumber.value.endsWith('222')) {
      inquiryError.value = 'Tagihan untuk periode ini sudah dilunasi.';
    } else if (customerNumber.value.endsWith('999')) {
      inquiryError.value = 'Layanan simulasi sedang mengalami timeout. Silakan coba kembali.';
    } else {
      inquiryError.value = err.response?.data?.message || err.response?.data?.error_message || 'ID pelanggan tidak ditemukan atau inquiry gagal.';
    }
  } finally {
    isChecking.value = false;
  }
}

function openReviewModal() {
  if (!inquiryResult.value) return;
  idempotencyKey.value = crypto.randomUUID();
  reviewModalOpen.value = true;
}

async function confirmPayment() {
  if (!inquiryResult.value || isSubmitting.value) return;

  isSubmitting.value = true;
  inquiryError.value = '';

  try {
    const res = await axios.post(route('pln-postpaid.mock-transactions.store'), {
      inquiry_reference: inquiryResult.value.inquiry_reference,
      idempotency_key: idempotencyKey.value,
    });

    if (res.data && (res.data.success || res.data.status === 'success')) {
      paymentResult.value = res.data;
      reviewModalOpen.value = false;
      resultModalOpen.value = true;

      router.reload({ only: ['transactions'] });
    } else {
      inquiryError.value = res.data.message || res.data.error_message || 'Simulasi pembayaran tagihan gagal.';
    }
  } catch (err) {
    inquiryError.value = err.response?.data?.message || 'Terjadi kesalahan saat simulasi pembayaran tagihan.';
  } finally {
    isSubmitting.value = false;
  }
}

function resetFlow() {
  customerNumber.value = '';
  inquiryResult.value = null;
  inquiryError.value = '';
  paymentResult.value = null;
  reviewModalOpen.value = false;
  resultModalOpen.value = false;
}

function maskIdentifier(id) {
  if (!id) return '';
  if (id.length <= 6) return id;
  return id.substring(0, 4) + '****' + id.substring(id.length - 4);
}
</script>

<template>
  <div class="space-y-6">
    <!-- Input Card -->
    <div class="p-6 rounded-3xl bg-slate-50 border border-slate-200 space-y-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
            <Zap class="w-4 h-4" />
          </div>
          <div>
            <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Layanan Listrik PLN</span>
            <h4 class="font-black text-sm text-slate-900">PLN Pascabayar (Tagihan Listrik)</h4>
          </div>
        </div>

        <span v-if="ppobConfig.mock_mode" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase">
          MODE SIMULASI
        </span>
      </div>

      <div class="space-y-3">
        <label for="pln_postpaid_customer_no" class="block text-xs font-bold text-slate-700">
          ID Pelanggan / Nomor Meter <span class="text-rose-500">*</span>
        </label>

        <div class="flex flex-col sm:flex-row gap-3">
          <div class="relative flex-1">
            <input
              id="pln_postpaid_customer_no"
              :value="customerNumber"
              @input="onInput"
              @keyup.enter.prevent="runInquiry"
              type="tel"
              placeholder="Masukkan 9–16 digit ID Pelanggan PLN"
              class="w-full px-4 py-3 min-h-[44px] rounded-2xl border border-slate-300 text-sm font-semibold tracking-wide bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition outline-none"
            />
          </div>

          <button
            @click="runInquiry"
            :disabled="!isValidCustomerNumber || isChecking"
            type="button"
            class="px-6 py-3 min-h-[44px] rounded-2xl text-xs font-bold transition flex items-center justify-center gap-2 shadow-xs shrink-0 w-full sm:w-auto"
            :class="isValidCustomerNumber && !isChecking
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'"
          >
            <RefreshCw v-if="isChecking" class="w-4 h-4 animate-spin" />
            <Search v-else class="w-4 h-4" />
            <span>{{ isChecking ? 'Mengecek...' : 'Cek Tagihan' }}</span>
          </button>
        </div>

        <p class="text-[11px] text-slate-500">
          Masukkan ID Pelanggan PLN Pascabayar untuk mengecek rincian tagihan listrik.
        </p>

        <!-- Bill Details Panel -->
        <div v-if="inquiryResult" class="p-5 rounded-3xl bg-indigo-50/90 border border-indigo-200 text-indigo-950 space-y-4 shadow-2xs">
          <div class="flex items-center justify-between border-b border-indigo-200/80 pb-3">
            <div class="flex items-center gap-2">
              <FileText class="w-5 h-5 text-indigo-600 shrink-0" />
              <span class="font-bold text-xs">Detail Tagihan Listrik PLN</span>
            </div>
            <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200/80 text-amber-900 uppercase">
              MODE SIMULASI
            </span>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs bg-white/90 p-4 rounded-2xl border border-indigo-100 font-medium">
            <div>
              <span class="text-[10px] text-slate-500 block">ID Pelanggan</span>
              <span class="font-mono font-bold text-slate-900">{{ inquiryResult.customer_number }}</span>
            </div>
            <div>
              <span class="text-[10px] text-slate-500 block">Nama Pelanggan</span>
              <span class="font-bold text-slate-900">{{ inquiryResult.customer_name }}</span>
            </div>
            <div>
              <span class="text-[10px] text-slate-500 block">Tarif / Daya</span>
              <span class="font-bold text-slate-900">{{ inquiryResult.tariff }} / {{ inquiryResult.power_va }} VA</span>
            </div>
            <div>
              <span class="text-[10px] text-slate-500 block">Periode Tagihan</span>
              <span class="font-extrabold text-indigo-600">{{ inquiryResult.billing_period }}</span>
            </div>
            <div>
              <span class="text-[10px] text-slate-500 block">Stand Meter</span>
              <span class="font-mono text-slate-800">
                {{ inquiryResult.meter_reading?.previous }} - {{ inquiryResult.meter_reading?.current }}
              </span>
            </div>
            <div>
              <span class="text-[10px] text-slate-500 block">Ref Inquiry</span>
              <span class="font-mono text-[10px] text-slate-600 truncate block">{{ inquiryResult.inquiry_reference }}</span>
            </div>
          </div>

          <!-- Price Calculation -->
          <div class="p-3.5 rounded-2xl bg-white border border-indigo-200/80 space-y-2 text-xs">
            <div class="flex justify-between text-slate-600">
              <span>Tagihan Listrik:</span>
              <span class="font-bold text-slate-900">{{ formatRupiah(inquiryResult.bill_amount_minor) }}</span>
            </div>
            <div class="flex justify-between text-slate-600">
              <span>Biaya Admin:</span>
              <span class="font-bold text-slate-900">{{ formatRupiah(inquiryResult.admin_fee_minor) }}</span>
            </div>
            <div class="flex justify-between border-t border-slate-200 pt-2 text-sm">
              <span class="font-bold text-slate-900">Total Pembayaran:</span>
              <span class="font-black text-indigo-600">{{ formatRupiah(inquiryResult.total_amount_minor) }}</span>
            </div>
          </div>

          <p class="text-[11px] text-indigo-800/90 italic">
            * Data tagihan ini merupakan simulasi lokal.
          </p>
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="inquiryError" class="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs flex items-center gap-2">
      <AlertTriangle class="w-4 h-4 text-rose-600 shrink-0" />
      <span>{{ inquiryError }}</span>
    </div>

    <!-- Action Trigger for Review Modal -->
    <div v-if="inquiryResult" class="p-5 rounded-2xl bg-indigo-900 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-lg">
      <div>
        <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Pembayaran PLN Pascabayar</span>
        <span class="text-lg font-black text-amber-300">{{ inquiryResult.customer_name }}</span>
        <p class="text-xs text-indigo-200 mt-0.5">Total: {{ formatRupiah(inquiryResult.total_amount_minor) }}</p>
      </div>

      <button
        @click="openReviewModal"
        type="button"
        class="w-full sm:w-auto px-6 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs transition shadow-md flex items-center justify-center gap-2 cursor-pointer"
      >
        <span>Lanjutkan Simulasi Pembayaran</span>
        <ArrowRight class="w-4 h-4" />
      </button>
    </div>

    <!-- Review Payment Modal -->
    <div v-if="reviewModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
          <div class="flex items-center gap-2">
            <span class="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-extrabold text-[10px] uppercase">
              MODE SIMULASI
            </span>
            <h3 class="font-bold text-sm text-slate-900">Review Pembayaran Tagihan</h3>
          </div>
          <button @click="reviewModalOpen = false" class="text-slate-400 hover:text-slate-600">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div class="space-y-3 text-xs">
          <div class="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
            <div class="flex justify-between">
              <span class="text-slate-500">ID Pelanggan:</span>
              <span class="font-mono font-bold text-slate-900">{{ inquiryResult?.customer_number }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Nama Pelanggan:</span>
              <span class="font-bold text-slate-900">{{ inquiryResult?.customer_name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Periode Tagihan:</span>
              <span class="font-bold text-indigo-600">{{ inquiryResult?.billing_period }}</span>
            </div>
            <div class="flex justify-between border-t border-slate-200 pt-2">
              <span class="text-slate-500">Tagihan Listrik:</span>
              <span class="font-bold text-slate-900">{{ formatRupiah(inquiryResult?.bill_amount_minor) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Biaya Admin:</span>
              <span class="font-bold text-slate-900">{{ formatRupiah(inquiryResult?.admin_fee_minor) }}</span>
            </div>
            <div class="flex justify-between border-t border-slate-200 pt-2">
              <span class="text-slate-500 font-bold">Total Pembayaran:</span>
              <span class="font-black text-slate-900 text-sm">{{ formatRupiah(inquiryResult?.total_amount_minor) }}</span>
            </div>
          </div>

          <div class="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed flex items-start gap-2.5">
            <Info class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>Pembayaran simulasi tidak memotong saldo dan tidak melunasi tagihan nyata.</span>
          </div>
        </div>

        <div class="flex items-center gap-3 pt-2">
          <button
            @click="reviewModalOpen = false"
            type="button"
            class="flex-1 py-3 rounded-2xl border border-slate-300 text-slate-700 font-bold text-xs hover:bg-slate-50 transition"
          >
            Kembali
          </button>
          <button
            @click="confirmPayment"
            :disabled="isSubmitting"
            type="button"
            class="flex-1 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <RefreshCw v-if="isSubmitting" class="w-4 h-4 animate-spin" />
            <span>{{ isSubmitting ? 'Memproses...' : 'Bayar Simulasi' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Payment Result Modal -->
    <div v-if="resultModalOpen && paymentResult" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="text-center space-y-2">
          <div class="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-xs">
            <CheckCircle2 class="w-7 h-7" />
          </div>
          <h3 class="text-lg font-black text-slate-900">Simulasi pembayaran berhasil</h3>
          <span class="inline-block px-3 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-extrabold uppercase">
            SIMULASI LOKAL
          </span>
        </div>

        <!-- Payment Ref Card -->
        <div class="p-5 rounded-2xl bg-slate-900 text-white text-center space-y-2 shadow-md">
          <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">NOMOR REFERENSI BAYAR SIMULASI</span>
          <div class="font-mono text-sm sm:text-base font-black text-amber-300 tracking-wider">
            {{ paymentResult.payment_reference }}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-4 rounded-2xl border border-slate-200">
          <div>
            <span class="text-[10px] text-slate-500 block">No. Transaksi</span>
            <span class="font-mono font-bold text-slate-900 truncate block">{{ paymentResult.transaction_reference }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Layanan</span>
            <span class="font-bold text-indigo-600">PLN Pascabayar</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">ID Pelanggan</span>
            <span class="font-mono font-bold text-slate-900">{{ maskIdentifier(paymentResult.customer_number) }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Nama Pelanggan</span>
            <span class="font-bold text-slate-900">{{ paymentResult.customer_name }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Periode Tagihan</span>
            <span class="font-bold text-slate-900">{{ paymentResult.billing_period }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Total Bayar</span>
            <span class="font-bold text-slate-900">{{ formatRupiah(paymentResult.total_amount_minor) }}</span>
          </div>
        </div>

        <p class="text-[11px] text-slate-500 italic text-center">
          * Tagihan PLN tidak benar-benar dibayar.
        </p>

        <button
          @click="resetFlow"
          type="button"
          class="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition cursor-pointer"
        >
          Transaksi Baru
        </button>
      </div>
    </div>
  </div>
</template>
